import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useListSystemRolesQuery,
  useListTenantRolesQuery,
  useCreateTenantRoleMutation,
  useUpdateTenantRoleMutation,
  useDeleteTenantRoleMutation,
  TenantRole,
} from '@/redux/internal-services/onboardingApi';
import { useIsTenantAdmin } from '@/hooks/useIsTenantAdmin';
import { Modal } from '@/components/UI/Modal';
import { fieldClass } from '@/components/UI/fieldStyles';
import PageShell from '@/components/Layout/PageShell';
import { PageHeader } from '@/components/UI/PageHeader';
import { Badge } from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';
import { Chip } from '@/components/UI/Chip';
import { EmptyState } from '@/components/UI/EmptyState';
import { LoadingState } from '@/components/UI/LoadingState';

const PermissionChip: React.FC<{ perm: string }> = ({ perm }) => (
  <Badge variant="accent">{perm}</Badge>
);

/** Lock icon shown next to the Tenant Admin checkbox and chip labels. */
const LockIcon: React.FC<{ className?: string }> = ({ className = 'w-3.5 h-3.5' }) => (
  <svg viewBox="0 0 16 16" fill="currentColor" className={className} aria-hidden="true">
    <path d="M11 7V5a3 3 0 10-6 0v2H4a1 1 0 00-1 1v6a1 1 0 001 1h8a1 1 0 001-1V8a1 1 0 00-1-1h-1zm-4-2a1 1 0 112 0v2H7V5zm1 6a1 1 0 110-2 1 1 0 010 2z" />
  </svg>
);

const RolesPage: React.FC = () => {
  const { t } = useTranslation();
  const isTenantAdmin = useIsTenantAdmin();
  const { data: sysData, isLoading: sysLoading } = useListSystemRolesQuery();
  const { data: tenData, isLoading: tenLoading, refetch } = useListTenantRolesQuery();
  const [createRole, { isLoading: creating }] = useCreateTenantRoleMutation();
  const [updateRole] = useUpdateTenantRoleMutation();
  const [deleteRole] = useDeleteTenantRoleMutation();

  const systemRoles = sysData?.data ?? [];
  const tenantRoles = tenData?.data ?? [];

  // ── Inline name editing — tracked for every role (default + custom) ────────
  const [editingNames, setEditingNames] = useState<Record<string, string>>({});
  const [nameErrors, setNameErrors] = useState<Record<string, string>>({});
  const [nameSaving, setNameSaving] = useState<Record<string, boolean>>({});

  // Initialise editing names once tenant roles load; add new rows without resetting existing edits.
  useEffect(() => {
    if (!tenantRoles.length) return;
    setEditingNames((prev) => {
      const next = { ...prev };
      for (const r of tenantRoles) {
        if (!(r.tenant_role_id in next)) {
          next[r.tenant_role_id] = r.name;
        }
      }
      return next;
    });
  }, [tenantRoles]);

  const handleNameSave = async (role: TenantRole) => {
    const trimmed = (editingNames[role.tenant_role_id] ?? '').trim();
    if (!trimmed) return;

    // Frontend duplicate check — compare against all OTHER roles (case-insensitive, trimmed).
    const duplicate = tenantRoles.find(
      (r) =>
        r.tenant_role_id !== role.tenant_role_id &&
        r.name.trim().toLowerCase() === trimmed.toLowerCase()
    );
    if (duplicate) {
      setNameErrors((e) => ({ ...e, [role.tenant_role_id]: `"${duplicate.name}" already exists. Choose a different name.` }));
      return;
    }
    setNameErrors((e) => ({ ...e, [role.tenant_role_id]: '' }));
    setNameSaving((s) => ({ ...s, [role.tenant_role_id]: true }));
    try {
      await updateRole({ id: role.tenant_role_id, data: { name: trimmed } }).unwrap();
      // Reflect saved name immediately in local state (avoids full refetch flicker).
      setEditingNames((n) => ({ ...n, [role.tenant_role_id]: trimmed }));
      refetch();
    } catch (err: unknown) {
      const code = (err as { data?: { message_code?: number } })?.data?.message_code;
      const msg = code === 3708
        ? 'A role with this name already exists.'
        : 'Failed to save. Please try again.';
      setNameErrors((e) => ({ ...e, [role.tenant_role_id]: msg }));
    } finally {
      setNameSaving((s) => ({ ...s, [role.tenant_role_id]: false }));
    }
  };

  // ── Create form state ──────────────────────────────────────────────────────
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newMappings, setNewMappings] = useState<string[]>([]);
  const [newVisibility, setNewVisibility] = useState(true);
  const [error, setError] = useState('');

  const tenantSystemRoleIds = systemRoles.filter((r) => r.type === 'tenant').map((r) => r.role_id);

  const handleCreate = async () => {
    if (!newName.trim() || newMappings.length === 0) {
      setError(t('admin.roleFormRequiredError'));
      return;
    }
    // Duplicate check for new role
    const trimmedNew = newName.trim();
    const dup = tenantRoles.find((r) => r.name.trim().toLowerCase() === trimmedNew.toLowerCase());
    if (dup) {
      setError(`A role named "${dup.name}" already exists.`);
      return;
    }
    setError('');
    try {
      await createRole({ name: trimmedNew, description: newDesc, system_role_mappings: newMappings, visibility: newVisibility }).unwrap();
      setShowCreate(false);
      setNewName('');
      setNewDesc('');
      setNewMappings([]);
      refetch();
    } catch (err: unknown) {
      const code = (err as { data?: { message_code?: number } })?.data?.message_code;
      const msg = code === 3708 ? 'A role with this name already exists.' : t('admin.createRoleFailed');
      setError(msg);
    }
  };

  const toggleMapping = (roleId: string) => {
    setNewMappings((prev) => prev.includes(roleId) ? prev.filter((r) => r !== roleId) : [...prev, roleId]);
  };

  const handleToggleVisibility = async (role: TenantRole) => {
    await updateRole({ id: role.tenant_role_id, data: { visibility: !role.visibility } }).unwrap().catch(() => { });
    refetch();
  };

  const handleDelete = async (roleId: string) => {
    if (!window.confirm(t('admin.confirmDeleteRole'))) return;
    await deleteRole(roleId).unwrap().catch(() => { });
    refetch();
  };

  return (
    <PageShell
      maxWidth="max-w-4xl"
      header={<PageHeader title="Role Management" subtitle="Manage system roles and create custom tenant roles." />}
    >
      {/* System Roles */}
      <section className="mb-8">
        <h2 className="text-h3 text-ui-text mb-3">System Roles</h2>
        <p className="text-sm text-ui-text3 mb-4">Centrally managed by the platform. Permissions propagate automatically.</p>
        {sysLoading ? (
          <LoadingState label="Loading..." className="py-8" />
        ) : (
          <div className="space-y-3">
            {systemRoles.map((role) => (
              <div key={role.role_id} className="card-surface p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <span className="font-medium text-ui-text">{role.name}</span>
                    <Badge variant="neutral" className="ml-2">{role.type}</Badge>
                  </div>
                </div>
                {role.description && <p className="text-sm text-ui-text2 mb-2">{role.description}</p>}
                <div className="flex flex-wrap gap-1.5">
                  {role.permissions.map((p) => <PermissionChip key={p} perm={p} />)}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Tenant Custom Roles */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-h3 text-ui-text">Custom Roles</h2>
          <Button size="sm" onClick={() => setShowCreate(true)}>
            + Create Role
          </Button>
        </div>
        <p className="text-sm text-ui-text3 mb-4">Tenant-specific roles built by combining system roles. Permissions are inherited automatically.</p>

        {tenLoading ? (
          <LoadingState label="Loading..." className="py-8" />
        ) : tenantRoles.length === 0 ? (
          <EmptyState
            variant="inline"
            className="py-8"
            title="No custom roles yet. Create one to customize access for your team."
          />
        ) : (
          <div className="space-y-3">
            {tenantRoles.map((role) => {
              const isTenantAdminRole = role.system_role_mappings.includes('tenant_admin');
              const currentName = editingNames[role.tenant_role_id] ?? role.name;
              const trimmedName = currentName.trim();
              const isDirty = trimmedName !== role.name;
              const saveDisabled = !trimmedName || nameSaving[role.tenant_role_id];

              return (
                <div key={role.tenant_role_id} className="card-surface p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0 mr-4">
                      {/* Inline name editor — works for ALL roles including defaults */}
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <input
                          type="text"
                          value={currentName}
                          onChange={(e) => {
                            setEditingNames((n) => ({ ...n, [role.tenant_role_id]: e.target.value }));
                            setNameErrors((err) => ({ ...err, [role.tenant_role_id]: '' }));
                          }}
                          className={`${fieldClass({ size: 'sm' })} min-w-0 max-w-xs flex-1 font-medium`}
                          aria-label={`Edit name for ${role.name}`}
                        />
                        {role.is_default && <Badge variant="accent" className="shrink-0">Default</Badge>}
                        {isDirty && (
                          <Button
                            type="button"
                            size="xs"
                            disabled={!!saveDisabled}
                            onClick={() => handleNameSave(role)}
                            className="shrink-0"
                          >
                            {nameSaving[role.tenant_role_id] ? 'Saving…' : 'Save'}
                          </Button>
                        )}
                      </div>
                      {nameErrors[role.tenant_role_id] && (
                        <p className="text-xs text-danger-text mb-1">{nameErrors[role.tenant_role_id]}</p>
                      )}
                      {role.description && <p className="text-sm text-ui-text2 mb-2">{role.description}</p>}
                      <div className="flex flex-wrap gap-1.5">
                        {role.system_role_mappings.map((m) => (
                          <Badge key={m} variant="accent">
                            → {m}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Visibility checkbox + delete */}
                    <div className="flex items-center gap-2 ml-auto shrink-0">
                      {/* Tenant Admin checkbox: always checked + locked for the logged-in admin */}
                      {isTenantAdminRole && isTenantAdmin ? (
                        <div className="flex items-center gap-1" title="Your Tenant Admin role is always active — it keeps your admin access.">
                          <div
                            className="w-5 h-5 rounded-chip border-2 flex items-center justify-center opacity-60 cursor-not-allowed bg-accent-fill border-accent-fill"
                            aria-checked="true"
                            role="checkbox"
                            aria-label="Tenant Admin always active"
                          >
                            <svg viewBox="0 0 12 12" fill="none" className="w-3 h-3">
                              <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </div>
                          <LockIcon className="w-3.5 h-3.5 text-ui-text3" />
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleToggleVisibility(role)}
                          aria-checked={role.visibility}
                          role="checkbox"
                          title={role.visibility ? 'Visible — click to hide' : 'Hidden — click to show'}
                          className={`w-5 h-5 rounded-chip border-2 flex items-center justify-center flex-shrink-0 transition-colors focus-ring ${
                            role.visibility ? 'bg-accent-fill border-accent-fill' : 'bg-transparent border-ui-border3'
                          }`}
                        >
                          {role.visibility && (
                            <svg viewBox="0 0 12 12" fill="none" className="w-3 h-3">
                              <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </button>
                      )}

                      {!role.is_default && (
                        <Button
                          variant="destructive-ghost"
                          size="xs"
                          onClick={() => handleDelete(role.tenant_role_id)}
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Self-lock note below the Tenant Admin row */}
                  {isTenantAdminRole && isTenantAdmin && (
                    <p className="mt-2 text-xs text-ui-text3 flex items-center gap-1">
                      <LockIcon className="w-3 h-3 shrink-0" />
                      This role is always active for you — it keeps your admin access.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Create Role Modal */}
      {showCreate && (
        <Modal
          title="Create Custom Role"
          onClose={() => { setShowCreate(false); setError(''); }}
          onSave={handleCreate}
          saving={creating}
          saveLabel="Create Role"
          maxWidthClassName="max-w-md"
        >
          <div>
            <label className="block text-sm font-medium text-ui-text mb-1">Role Name</label>
            <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)}
              className={fieldClass()} placeholder="e.g. Senior Estimator" />
          </div>

          <div>
            <label className="block text-sm font-medium text-ui-text mb-1">Description (optional)</label>
            <input type="text" value={newDesc} onChange={(e) => setNewDesc(e.target.value)}
              className={fieldClass()} />
          </div>

          <div>
            <label className="block text-sm font-medium text-ui-text mb-2">Map to System Roles (select one or more)</label>
            <div className="flex flex-wrap gap-2">
              {tenantSystemRoleIds.map((rid) => (
                <Chip key={rid} selected={newMappings.includes(rid)} onClick={() => toggleMapping(rid)}>
                  {rid}
                </Chip>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={newVisibility} onChange={(e) => setNewVisibility(e.target.checked)} className="w-4 h-4 accent-accent-fill" />
            <span className="text-sm text-ui-text2">Show in role selection (hide mapped system roles)</span>
          </label>

          {error && <p className="text-danger-text text-sm">{error}</p>}
        </Modal>
      )}
    </PageShell>
  );
};

export default RolesPage;
